import { CommonModule } from '@angular/common'
import { Component, inject, signal, computed } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { GroupType } from '@mattrika/benefit-model'
import { NgIcon } from '@ng-icons/core'
import { AuthStateService } from '../../auth/auth-state.service'
import { LoginFormService } from '../../services/login-form.service'

@Component({
    selector: 'lib-page-login',
    imports: [CommonModule, ReactiveFormsModule, RouterModule, NgIcon],
    templateUrl: './page-login.component.html',
    styleUrls: ['./page-login.component.css'],
    providers: [LoginFormService],
})
export class PageLoginComponent {
    public readonly loginFormService = inject(LoginFormService)
    private readonly authStateService = inject(AuthStateService)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)

    readonly inputType = signal<'text' | 'password'>('password')
    readonly isSubmitting = signal(false)
    readonly errorMessage = signal('')
    readonly rememberMe = signal(false)

    readonly icon = computed(() =>
        this.inputType() === 'password' ? 'lucideEye' : 'lucideEyeOff',
    )

    togglePasswordVisibility() {
        this.inputType.update((current) =>
            current === 'password' ? 'text' : 'password',
        )
    }

    toggleRememberMe() {
        this.rememberMe.update((v) => !v)
    }

    onSubmit() {
        if (this.loginFormService.form.invalid) {
            this.loginFormService.form.markAllAsTouched()
            return
        }

        const { identifier, password } = this.loginFormService.getValue()
        this.isSubmitting.set(true)
        this.errorMessage.set('')

        this.loginFormService.form.disable()

        this.authStateService.login(identifier, password).subscribe({
            next: (response) => {
                this.isSubmitting.set(false)
                this.loginFormService.form.enable()

                if (response) {
                    const returnUrl =
                        this.route.snapshot.queryParamMap.get('returnUrl')
                    if (
                        returnUrl &&
                        returnUrl.startsWith('/') &&
                        !returnUrl.startsWith('//') &&
                        returnUrl !== '/login'
                    ) {
                        this.router.navigateByUrl(returnUrl)
                        return
                    }

                    this.redirectToDashboard()
                }
            },
            error: ({ error }) => {
                this.isSubmitting.set(false)
                this.loginFormService.form.enable()
                this.errorMessage.set(
                    error?.message ||
                        'Login failed. Please verify your credentials and try again.',
                )
            },
        })
    }

    private redirectToDashboard() {
        const groupType = this.authStateService.getGroupType()

        if (
            this.authStateService.isSuperAdmin() ||
            groupType === GroupType.superAdmin
        ) {
            this.router.navigate(['/admin'])
            return
        }

        if (groupType === GroupType.vendor) {
            this.router.navigate(['/vendor'])
            return
        }

        if (groupType === GroupType.client) {
            if (this.authStateService.isOwner()) {
                this.router.navigate(['/client-admin'])
            } else {
                this.router.navigate(['/'])
            }
            return
        }

        this.router.navigate(['/'])
    }
}
